import { toast } from "react-hot-toast";
import { clearAllTenantSessionData } from "./tenantContext";

export const API_BASE =
  (process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "http://localhost:8081/api");

const defaultHeaders = {
  "Content-Type": "application/json",
  "ngrok-skip-browser-warning": "true",
};

let isRedirectingToLogin = false;

export const resetLoginRedirectFlag = () => {
  isRedirectingToLogin = false;
};

/** Call before clearing session on intentional logout to suppress auth-error toasts. */
export const markLogoutInProgress = () => {
  isRedirectingToLogin = true;
};

/**
 * Unauthenticated endpoints — never attach JWT and never trigger session-expiry logout.
 * Wrong password / forgot-password failures must surface the API message as-is.
 */
const PUBLIC_AUTH_PATHS = [
  "/login",
  "/payment/login",
  "/auth/register",
  "/forget-password",
  "/forgot-password",
  "/reset-password",
  "/config",
];

const normalizeApiPath = (path = "") => {
  const bare = String(path).split("?")[0];
  return bare.startsWith("/") ? bare : `/${bare}`;
};

export const isPublicAuthPath = (path) => {
  const p = normalizeApiPath(path);
  return PUBLIC_AUTH_PATHS.some(
    (prefix) => p === prefix || p.startsWith(`${prefix}/`),
  );
};

/**
 * True only for JWT verification failures from protected routes.
 * Never matches credential errors ("Invalid email or password", etc.).
 */
const isJwtSessionError = (data, response) => {
  if (response.status !== 401) return false;
  const err = String(data?.error || data?.message || "").toLowerCase().trim();
  if (!err) return false;

  // Credential / account messages — never treat as expired session
  if (
    err.includes("invalid email") ||
    err.includes("invalid password") ||
    err.includes("email or password") ||
    err.includes("account is inactive") ||
    err.includes("tenant not found") ||
    err.includes("tenant is inactive") ||
    err.includes("access not allowed") ||
    err.includes("permission") ||
    err.includes("do not have permission")
  ) {
    return false;
  }

  return (
    err === "invalid token" ||
    err.includes("invalid token") ||
    err.includes("jwt expired") ||
    err.includes("token expired") ||
    err.includes("jwt malformed") ||
    err.includes("jwt must be provided") ||
    err.includes("invalid token type")
  );
};

const apiErrorMessage = (data, response, rawText) => {
  if (response.status === 413) {
    return (
      data?.error ||
      data?.message ||
      "File is too large. Maximum upload size is 20 MB per file."
    );
  }

  let message =
    data?.message || data?.error || `Request failed with status ${response.status}`;

  if (
    (!data?.message && !data?.error) ||
    (typeof rawText === "string" && rawText.includes("<!DOCTYPE html>"))
  ) {
    const cannotMatch = String(rawText).match(
      /Cannot\s+(GET|PUT|POST|PATCH|DELETE)\s+(\S+)/i,
    );
    if (cannotMatch) {
      message = `API route not found (${cannotMatch[1]} ${cannotMatch[2]}). Restart the backend server and try again.`;
    } else if (response.status === 404) {
      message =
        "Resource not found (404). The form or API route may be missing — restart the backend and try again.";
    }
  }

  return message;
};

/**
 * @param {string} path
 * @param {RequestInit & { skipAuth?: boolean }} [options]
 */
export const request = async (path, options = {}) => {
  const { skipAuth = false, _etagRetry = false, ...fetchOptions } = options;

  try {
    const user = JSON.parse(localStorage.getItem("currentUser") || "{}");
    const token = user.token || user.accessToken;
    const publicPath = skipAuth || isPublicAuthPath(path);

    const headers = {
      ...defaultHeaders,
      ...(fetchOptions.headers || {}),
    };

    // Never send a stale JWT on login / public auth — it confuses error handling
    // and is unused by those routes.
    if (token && !publicPath) {
      headers.Authorization = `${token}`;
    }

    const url = `${API_BASE}${path}`;

    const response = await fetch(url, {
      cache: "no-store",
      ...fetchOptions,
      headers,
    });

    if (response.status === 304) {
      if (_etagRetry) {
        return { status: 1, message: null, result: {} };
      }
      return request(path, { ...options, _etagRetry: true, cache: "reload" });
    }

    const rawText = await response.text();
    let data = {};
    if (rawText) {
      try {
        data = JSON.parse(rawText);
      } catch {
        data = {};
      }
    }

    if (!response.ok) {
      const message = apiErrorMessage(data, response, rawText);

      // Session logout ONLY on protected routes with a real JWT failure.
      if (!publicPath && isJwtSessionError(data, response)) {
        if (token && !isRedirectingToLogin) {
          isRedirectingToLogin = true;
          clearAllTenantSessionData();
          toast.error("Session expired. Please login again.");
          setTimeout(() => {
            window.location.href = "/login";
          }, 1500);
        }
        const error = new Error(message || "Session expired");
        error.status = 401;
        error.isAuthError = true;
        error.data = data;
        throw error;
      }

      const error = new Error(message);
      error.status = response.status;
      error.statusText = response.statusText;
      error.data = data;
      if (response.status === 403) {
        error.isPermissionError = true;
      }
      // Login / credential failures are not permission errors
      if (
        response.status === 401 &&
        !publicPath &&
        !isJwtSessionError(data, response)
      ) {
        error.isPermissionError = true;
      }
      throw error;
    }

    // Successful body that still flags an invalid token (legacy shape)
    if (
      !publicPath &&
      String(data?.error || "").toLowerCase() === "invalid token"
    ) {
      if (token && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        clearAllTenantSessionData();
        toast.error("Session expired. Please login again.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      }
      const error = new Error(data.message || data.error || "Session expired");
      error.status = 401;
      error.isAuthError = true;
      error.data = data;
      throw error;
    }

    return data;
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    if (error.message === "Failed to fetch" || error.name === "TypeError") {
      throw new Error(
        `Cannot connect to server at ${API_BASE}. Make sure the server is running.`,
      );
    }
    throw error;
  }
};
